
/* =========================================================
   WEBHAUS PAGE TRANSITION — V2.4
   Color + Static Logo + Custom Easing
   Browser History + 3-Second Failsafe
   ========================================================= */

(() => {
  'use strict';

  const transition = document.getElementById(
    'webhaus-page-transition'
  );

  if (!transition) return;

  const STORAGE_KEY = 'webhaus-page-transition';
  const START_KEY = 'webhaus-page-transition-started-at';

  const TRANSITION_DURATION = Math.max(
    0,
    parseInt(transition.dataset.duration, 10) || 500
  );

  const MAX_WAIT_TIME = 3000;

  let isTransitioning = false;
  let navigationTimer = null;
  let loadTimer = null;
  let revealTimer = null;
  let revealStarted = false;

  /*
   * ---------------------------------------------------------
   * HELPERS
   * ---------------------------------------------------------
   */

  function clearTimers() {
    if (navigationTimer !== null) {
      clearTimeout(navigationTimer);
      navigationTimer = null;
    }

    if (loadTimer !== null) {
      clearTimeout(loadTimer);
      loadTimer = null;
    }

    if (revealTimer !== null) {
      clearTimeout(revealTimer);
      revealTimer = null;
    }
  }

  function clearFlag() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(START_KEY);
    } catch (error) {}
  }

  function getTransitionStartTime() {
    try {
      const value = Number(
        sessionStorage.getItem(START_KEY)
      );

      return Number.isFinite(value) && value > 0
        ? value
        : Date.now();
    } catch (error) {
      return Date.now();
    }
  }

  /*
   * ---------------------------------------------------------
   * REVEAL PAGE
   * ---------------------------------------------------------
   */

  function revealPage() {
    if (revealStarted) return;

    revealStarted = true;

    if (loadTimer !== null) {
      clearTimeout(loadTimer);
      loadTimer = null;
    }

    /*
     * Remove the pending class to allow the configured
     * CSS opacity transition to fade the overlay out.
     */

    requestAnimationFrame(() => {
      document.documentElement.classList.remove(
        'webhaus-transition-pending'
      );

      transition.classList.remove('is-entering');

      /*
       * Keep the interaction lock until the fade finishes.
       */

      revealTimer = setTimeout(() => {
        revealTimer = null;

        document.body.classList.remove(
          'webhaus-transition-active'
        );

        isTransitioning = false;
      }, TRANSITION_DURATION);
    });
  }

  /*
   * ---------------------------------------------------------
   * HISTORY RESTORATION
   * ---------------------------------------------------------
   */

  function showHistoryTransition() {
    clearTimers();
    clearFlag();

    revealStarted = false;
    isTransitioning = true;

    document.body.classList.add(
      'webhaus-transition-active'
    );

    document.documentElement.classList.add(
      'webhaus-transition-pending'
    );

    transition.classList.add('is-entering');

    // Commit the visible overlay before fading it out.
    void transition.offsetWidth;

    requestAnimationFrame(() => {
      revealPage();
    });
  }

  /*
   * ---------------------------------------------------------
   * LINK VALIDATION
   * ---------------------------------------------------------
   */

  function isModifiedClick(event) {
    return (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    );
  }

  function isValidLink(link) {
    if (!link) return false;

    const href = link.getAttribute('href');

    if (!href) return false;

    if (
      /^(#|mailto:|tel:|javascript:|sms:|data:|blob:)/i.test(href)
    ) {
      return false;
    }

    if (link.hasAttribute('download')) return false;

    if (
      link.target &&
      link.target.toLowerCase() !== '_self'
    ) {
      return false;
    }

    if (
      link.rel &&
      link.rel.split(/\s+/).includes('external')
    ) {
      return false;
    }

    let url;

    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      return false;
    }

    if (
      url.protocol !== 'http:' &&
      url.protocol !== 'https:'
    ) {
      return false;
    }

    if (url.origin !== window.location.origin) {
      return false;
    }

    if (
      url.pathname === window.location.pathname &&
      url.search === window.location.search
    ) {
      return false;
    }

    // Preserve Shopify cart, checkout and account flows.
    if (
      /^\/(cart|checkout|account)(\/|$)/.test(url.pathname)
    ) {
      return false;
    }

    return true;
  }

  /*
   * ---------------------------------------------------------
   * INITIAL NAVIGATION DETECTION
   * ---------------------------------------------------------
   */

  let transitionPending = false;

  try {
    transitionPending =
      sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch (error) {}

  const navigationEntries =
    performance.getEntriesByType('navigation');

  const isBackForwardLoad =
    navigationEntries.length > 0 &&
    navigationEntries[0].type === 'back_forward';

  if (transitionPending) {
    const startedAt = getTransitionStartTime();

    // Consume the flag now that this document has read it.
    clearFlag();

    revealStarted = false;
    isTransitioning = true;

    document.body.classList.add(
      'webhaus-transition-active'
    );

    /*
     * Enforce a maximum of 3 seconds from the original
     * navigation click, including destination load time.
     */

    const elapsed = Math.max(0, Date.now() - startedAt);
    const remainingWait = Math.max(
      0,
      MAX_WAIT_TIME - elapsed
    );

    if (document.readyState === 'complete') {
      revealPage();
    } else {
      window.addEventListener('load', revealPage, {
        once: true
      });

      loadTimer = setTimeout(() => {
        revealPage();
      }, remainingWait);
    }
  } else if (isBackForwardLoad) {
    /*
     * Non-cached history navigation: the early head script
     * has already covered the destination. Apply the same
     * 3-second maximum wait.
     */

    revealStarted = false;
    isTransitioning = true;

    document.body.classList.add(
      'webhaus-transition-active'
    );

    if (document.readyState === 'complete') {
      revealPage();
    } else {
      window.addEventListener('load', revealPage, {
        once: true
      });

      loadTimer = setTimeout(() => {
        revealPage();
      }, MAX_WAIT_TIME);
    }
  } else {
    transition.classList.remove('is-entering');

    document.documentElement.classList.remove(
      'webhaus-transition-pending'
    );
  }

  /*
   * ---------------------------------------------------------
   * START NORMAL LINK TRANSITION
   * ---------------------------------------------------------
   */

  function startTransition() {
    if (isTransitioning) return false;

    isTransitioning = true;
    revealStarted = false;

    try {
      sessionStorage.setItem(STORAGE_KEY, 'true');
      sessionStorage.setItem(
        START_KEY,
        String(Date.now())
      );
    } catch (error) {}

    document.body.classList.add(
      'webhaus-transition-active'
    );

    transition.classList.add('is-entering');

    return true;
  }

  /*
   * ---------------------------------------------------------
   * INTERNAL LINK NAVIGATION
   * ---------------------------------------------------------
   */

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented) return;
      if (isModifiedClick(event)) return;

      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('a');

      if (!isValidLink(link)) return;

      if (
        link.closest(
          'quick-add-modal, ' +
          'cart-drawer, ' +
          'search-modal, ' +
          'dialog, ' +
          '[data-modal], ' +
          '[data-drawer], ' +
          'form'
        )
      ) {
        return;
      }

      if (!startTransition()) return;

      event.preventDefault();

      const destination = link.href;

      navigationTimer = setTimeout(() => {
        navigationTimer = null;

        window.location.assign(destination);
      }, TRANSITION_DURATION);
    },
    true
  );

  /*
   * ---------------------------------------------------------
   * BROWSER BACK / FORWARD + BFCACHE
   * ---------------------------------------------------------
   */

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    /*
     * The browser restored an already-rendered document.
     * Reveal it with the normal configured fade-out.
     */

    showHistoryTransition();
  });

  /*
   * Preserve the navigation flag on pagehide so the
   * destination can read it during normal navigation.
   */

  window.addEventListener('pagehide', () => {
    clearTimers();
  });

})();
